import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import AddMemberToQuote from '@components/assign-team-member/AddMemberToQuote';
import NoData from '@components/NoData';
import SampleComment from '@components/SampleComment';
import TimeBasedLoader from '@components/TimeBasedLoader';
import { toastError, toastSuccess } from '@components/Toast';
import Textarea from '@components/Textarea';

import useWindowSize from '@hooks/useWindowSize';

import { originalStatus } from '@features/costing/utils';
import EodAddModal from '@features/costing/components/EodAddModal';
import {
	allFilterPages,
	costingDetailsFilterFields,
	generateCostingDetailsParams
} from '@features/filter/static/filterUtil';
import FilterComponent from '@features/filter/v2/FilterComponent';
import CostingDetailsTopHeader from '@features/collection/components/CostingDetailsTopHeader';
import CostingTimeline from '@features/collection/components/CostingTimeline';
import ConfirmationModal from '@features/crm/components/ConfirmationModal';

import { clearCurrentActivePage, setCurrentActivePage } from '@store/actions/ActivePageAction';
import { clearDesignViewModalInfo, setDesignViewModalInfo } from '@store/actions/DesignViewModalAction';
import { clearDetailsPageGlobalFilter, handleFilterOfListPage } from '@store/actions/FilterAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { fetchDependentFilterList } from '@services/commonService';
import { onApproveRequest, onRejectRequest } from '@services/costingService';

import ApiClient from '@lib/apiClient';

import {
	_getKey,
	changeDateFormat,
	checkAnyUserType,
	checkUserType,
	downloadBlobFIle,
	getActionDoneBy,
	getColorWiseActivityStatusOfCosting,
	isValidInput,
	packTag,
	renderSetItemDesingDetailsWithImage,
	replaceSpace,
	validateFloatNumber
} from '@utils/Util';

import AddTaskModal from '@/components/AddTaskModal';
import { storeProductData } from '@/store/actions/DesignAction';
import CreateOrderModal from '@/features/order/components/CreateOrderModal';

const ROLES_FOR_DELETE = ['ACCOUNT_MANAGER', 'COSTING_MANAGER'];

const CostingDetails = () => {
	const [costingDetails, setCostingDetails] = useState(null);
	const [selectedInitialCostingId, setSelectedInitialCostingId] = useState(null);
	const [selectedProductId, setSelectedProductId] = useState(null);
	const [selectedProductIds, setSelectedProductIds] = useState([]);
	const [selectedTaskProductIds, setSelectedTaskProductIds] = useState([]);
	const [isShowCosting, setIsShowCosting] = useState(false);
	const [cellId, setCellId] = useState('');
	const [selectedIds, setSelectedIds] = useState([]);
	const [selectAll, setSelectAll] = useState(false);
	const [isDownloading, setIsDownloading] = useState(false);
	const [showComment, setShowComment] = useState(false);
	const [selectedIndex, setSelectedIndex] = useState(null);
	const [costingItem, setCostingItem] = useState([]);
	const [selectedId, setSelectedId] = useState(null);
	const [costingRequestDetails, setCostingRequestDetails] = useState({});
	const [isShowFilter, setIsShowFilter] = useState(true);
	const [showEodModal, setShowEodModal] = useState(false);
	const filterfields = useSelector((store) => store.filterFieldStore);
	const costingResponse = useSelector((store) => store.filterResponseStore);
	const navigate = useNavigate();
	const location = useLocation();
	const { id } = useParams();
	const dispatch = useDispatch();
	const prevDescription = useRef(null);
	const prevTarget = useRef(null);
	const prevOffer = useRef(null);
	const prevRemark = useRef(null);
	const elementRef = useRef(null);
	const productionPriceMissing = 'Production price is missing to move forward';
	const isAllSelected = costingDetails?.quoteItemResponseList?.length === selectedIds?.length;
	const [selectedQuoteItemId, setSelectedQuoteItemId] = useState(null);
	const [showCostingTimelineId, setShowCostingTimelineId] = useState(null);
	useWindowSize(() => setIsShowFilter(false));
	const [isShowTaskUpdateModal, setIsShowTaskUpdateModal] = useState(false);
	const [isOpenPlaceOrder, setIsOpenPlaceOrder] = useState(false);
	const [confirmationModal, setConfirmationModal] = useState({ isOpen: false, type: null });
	const [buyerPrice, setBuyerPrice] = useState(null);
	const [selectedQuantity, setSelectedQuantity] = useState(null);

	useEffect(() => {
		window.scrollTo(0, 0);
		const { ...paramsData } = generateCostingDetailsParams(filterfields);
		dispatch(openLoader());
		Promise.all([
			dispatch(
				fetchDependentFilterList({ type: 'COSTING_DETAILS', idUrl: `/${id}/filter/all` }, paramsData)
			),
			fetchCostingDetails()
		]).finally(() => dispatch(closeLoader()));
	}, [filterfields]);

	useEffect(() => {
		let searchParams = new URLSearchParams(location.search);
		let isShowComment = searchParams.get('showComment');
		let productId = searchParams.get('productId');
		let selectedQuote = searchParams.get('selectedQuote');
		if (selectedQuote) {
			setSelectedQuoteItemId(selectedQuote);
		}
		if (isShowComment && productId) {
			setSelectedId(productId);
		}
		return () => {
			dispatch(clearDetailsPageGlobalFilter());
			dispatch(clearCurrentActivePage());
		};
	}, [location.search]);

	useEffect(() => {
		setCostingDetails(costingResponse);
		setCostingRequestDetails({
			id: costingResponse?.id,
			collectionId: costingResponse?.collectionId,
			collectionName: costingResponse?.collectionName,
			referenceNumber: costingResponse?.referenceNumber,
			requestedBy: costingResponse?.requestedBy,
			requestedDate: costingResponse?.requestedDate,
			noOfDesign: costingResponse?.noOfDesign
		});
		setCostingItem(costingResponse?.quoteItemResponseList);
	}, [costingResponse]);

	const fetchCostingDetails = async () => {
		const { ...paramsData } = generateCostingDetailsParams(filterfields);
		await dispatch(handleFilterOfListPage({ type: 'quoteRequest', idUrl: `${id}` }, paramsData, false));
	};

	const onClearChat = () => {
		setCostingItem((prevQuoteStore) => {
			const updatedQuoteStore = prevQuoteStore?.map((item) => {
				if (item.productId === selectedId) {
					return {
						...item,
						noOfUnreadMessage: 0
					};
				}
				return item;
			});
			return updatedQuoteStore;
		});
		setSelectedId(null);
	};

	const handleCommentClose = () => {
		dispatch(clearCurrentActivePage());
		onClearChat();
		setShowComment(false);
	};

	const resetAll = () => {
		setCellId('');
		prevDescription.current = null;
		prevTarget.current = null;
		prevRemark.current = null;
		prevOffer.current = null;
	};

	const onSetCellId = (rowId, type) => setCellId(`${rowId}_${type}`);

	const markAll = (mark) => {
		if (mark) {
			const selectable = costingItem?.filter((i) => ['APPROVED'].includes(i?.quoteItemStatus)) || [];
			const ids = selectable.map((i) => i.quantityWiseCostingId.toString()) || [];
			setSelectedIds(ids);

			const productIds = selectable.map((i) => i.productId) || [];
			setSelectedTaskProductIds(productIds);
			setSelectedProductIds(productIds);
		} else {
			setSelectedIds([]);
			setSelectedTaskProductIds([]);
			setSelectedProductIds([]);
		}
	};

	const onChangeCheckbox = (e, item) => {
		const { quantityWiseCostingId, productId } = item;
		let temp = [...selectedIds];
		let tempProductIds = [...selectedProductIds];

		if (e.target.checked) {
			temp = [...temp, quantityWiseCostingId.toString()];
			tempProductIds = [...tempProductIds, productId.toString()];
		} else {
			temp = temp.filter((id) => id !== quantityWiseCostingId.toString());
			tempProductIds = tempProductIds.filter((id) => id !== productId.toString());
		}

		setSelectedIds(temp);
		setSelectedProductIds(tempProductIds);
		setSelectedTaskProductIds(tempProductIds);
	};

	const onChangeItem = (e, selectedItem) => {
		const { name, value } = e.target;
		let updatedItem = costingItem.map((item) => {
			if (item.quantityWiseCostingId === selectedItem.quantityWiseCostingId) {
				item[name] = value;
			}
			return item;
		});
		setCostingDetails({ ...costingDetails, quoteItemResponseList: updatedItem });
	};

	// const onRequestClose = async () => {
	// 	dispatch(openLoader());
	// 	try {
	// 		let { data } = await ApiClient.POST('quoteRequest', {}, `${costingDetails.id}/archive`);
	// 		setCellId('');
	// 		dispatch(closeLoader());
	// 		if (data.success) {
	// 			toastSuccess(data.message);
	// 		}
	// 	} catch (error) {
	// 		dispatch(closeLoader());
	// 		toastError(error.response.data.message);
	// 	}
	// };

	const onUpdateItem = async (selectedItem) => {
		let body = selectedItem;
		dispatch(openLoader());
		try {
			let { data } = await ApiClient.POST('quantityWiseUpdatePrice', body);
			setCellId('');
			fetchCostingDetails();
			dispatch(closeLoader());
			if (data.success) {
				toastSuccess(data.message);
				resetAll();
			}
		} catch (error) {
			dispatch(closeLoader());
			toastError(error.response.data.message);
		}
	};

	const onDeleteQuoteItem = async (quoteItemId) => {
		dispatch(openLoader());
		try {
			let { data } = await ApiClient.DELETE('quoteRequest', {}, `${id}/item/${quoteItemId}`);
			dispatch(closeLoader());
			if (data) {
				fetchCostingDetails();
				toastSuccess(data.message);
			}
		} catch (error) {
			dispatch(closeLoader());
			toastError(error.response.data.message);
		}
	};

	const onUpdateVariant = async (selectedItem) => {
		let body = selectedItem;
		dispatch(openLoader());
		try {
			let { data } = await ApiClient.PUT('quoteRequest', body, `${costingDetails.id}/update-variant`);
			setCellId('');
			dispatch(closeLoader());
			if (data.success) {
				resetAll();
				toastSuccess(data.message);
			}
		} catch (error) {
			dispatch(closeLoader());
			toastError(error.response.data.message);
		}
	};

	const onAddNewVariant = async (item) => {
		dispatch(openLoader());
		try {
			let { data } = await ApiClient.POST(
				'quoteRequest',
				{},
				`${costingDetails.id}/item/${item.id}/add-variant`
			);
			fetchCostingDetails();
			dispatch(closeLoader());
			if (data.success) {
				setCellId(`${data?.payload?.quantityWiseCostingId}_VARIANT`);
				toastSuccess(data.message);
			}
		} catch (error) {
			dispatch(closeLoader());
			toastError(error.response.data.message);
		}
	};

	// const onPlaceOrder = async () => {
	// 	const productWithoutBodyFabric = getProductWithNoBodyFabric();
	// 	const soldArtworks = getProductWithSoldArtwork();

	// 	if (soldArtworks) {
	// 		toastWarning(
	// 			'This artwork has already been sold and cannot be ordered. Please select a different artwork.'
	// 		);
	// 		return;
	// 	}

	// 	if (productWithoutBodyFabric.length > 0) {
	// 		dispatch(openLoader());
	// 		dispatch({
	// 			type: 'SET_PRODUCT_DATA',
	// 			payload: {
	// 				key: 'productsWithoutBodyFabric',
	// 				data: productWithoutBodyFabric
	// 			}
	// 		});
	// 		toastError('Please add body fabric for place order.');
	// 		dispatch(closeLoader());
	// 	} else {
	// 		// Check if all selected items have production prices
	// 		const selectedItems = costingItem?.filter((item) =>
	// 			selectedIds.includes(item?.quantityWiseCostingId.toString())
	// 		);

	// 		if (!selectedItems.every((item) => item?.productionPrice)) {
	// 			toastError('All selected items must have production prices to place an order.');
	// 			return;
	// 		}

	// 		// Set selected product IDs for the modal
	// 		const productIds = selectedItems.map((item) => item.productId);
	// 		await dispatch(storeProductData('selectedProductIds', productIds));

	// 		setIsOpenPlaceOrder(true);
	// 	}
	// };

	const onSendOffer = async () => {
		dispatch(openLoader());
		try {
			let { data } = await ApiClient.POST('sendOffer', {}, `?ids=${selectedIds}`);
			setCellId('');
			dispatch(closeLoader());
			if (data.success) {
				fetchCostingDetails();
				toastSuccess(data.message);
				setSelectedIds([]);
				setSelectAll(false);
			}
		} catch (error) {
			dispatch(closeLoader());
			toastError(error.response.data.message);
		}
	};

	const downloadSheet = async () => {
		setIsDownloading(true);
		let params = `${costingDetails.id}/download-cost-sheet-in-excel?quantityWiseIds=${selectedIds}`;
		ApiClient.GET_FILE_DATA('quoteRequest', params)
			.then((response) => {
				downloadBlobFIle(response);
				setIsDownloading(false);
			})
			.catch(() => {
				toastError('Something went wrong!');
				setIsDownloading(false);
			});
	};

	const getStatus = (status) => {
		if (status === 'APPROVED') return 'green';
		return 'secondary';
	};

	const getOfferPrice = (item) => (
		<div className={`regular-14 ${getStatus(item.status)} weight-500`}>
			{item?.adminOfferPrice && (
				<span className="d-block">
					{item.status === 'OFFER_SENT' && (
						<img
							src={`${process.env.PUBLIC_URL}/icons/arrow_forward.svg`}
							className="offer-sent"
						/>
					)}
					{item.status === 'REQUEST_FOR_REVISION' ? (
						<span className="regular-12 red weight-500">
							${parseFloat(item?.adminOfferPrice).toFixed(2)}
						</span>
					) : (
						<>${parseFloat(item?.adminOfferPrice).toFixed(2)}</>
					)}
					{item?.margin >= 0 && <span className="pl-1 gray_dark_01">({item?.margin}%)</span>}
				</span>
			)}
			{item?.priceUpdateDTOArray?.length > 0 && (
				<p className="regular-12 gray_dark_02 weight-500">
					<del>
						$
						{parseFloat(
							item?.priceUpdateDTOArray[item?.priceUpdateDTOArray?.length - 1]?.previousValue
						).toFixed(2)}
					</del>
					{item?.priceUpdateDTOArray[item?.priceUpdateDTOArray?.length - 1]?.margin >= 0 && (
						<span className="pl-1 gray_dark_01">
							({item?.priceUpdateDTOArray[item?.priceUpdateDTOArray?.length - 1]?.margin}%)
						</span>
					)}
				</p>
			)}
		</div>
	);

	const isCostingManger = () => checkUserType('COSTING_MANAGER');
	const isShowDeleteButton = () => {
		return checkAnyUserType(ROLES_FOR_DELETE);
	};

	// The onRemoveVariant function will be triggered only if a new variant was created, and it will remove that variant.
	const onRemoveVariant = async (selectedVariant) => {
		dispatch(openLoader());
		try {
			let { data } = await ApiClient.DELETE(
				'removeVariant',
				{},
				`${selectedVariant.id}/delete-variant`
			);
			dispatch(closeLoader());
			if (data.success) {
				fetchCostingDetails();
				resetAll();
				toastSuccess(data.message);
			}
		} catch (error) {
			dispatch(closeLoader());
			toastError(error.response.data.message);
		}
	};

	useEffect(() => {
		if (selectedProductId !== null) {
			const findSetItem = costingItem.find((item) => selectedProductId === item.productId);
			openDesignViewModal(findSetItem);
		}
	}, [selectedProductId]);

	const openDesignViewModal = (findSetItem) => {
		dispatch(
			setDesignViewModalInfo({
				mergeProductList:
					findSetItem &&
					findSetItem?.setMemberList &&
					findSetItem?.setMemberList.map((member) => member.productId),
				isShowMergeTechPack: findSetItem?.isSet,
				id: selectedProductId,
				onCloseViewModal: () => {
					setSelectedInitialCostingId(null);
					setSelectedProductId(null);
					setSelectedIndex(null);
					setIsShowCosting(false);
					dispatch(clearDesignViewModalInfo({}));
					setSelectedQuantity(null);
				},
				designIdList: [...new Set(costingItem?.map((data) => data.productId))],
				source: 'PRODUCT_LIST',
				isCostingRequestFromBuyer: true,
				initialCostingId: selectedInitialCostingId,
				onUpdateCostingStatus: () => fetchCostingDetails(),
				costingItemList: costingItem,
				selectedIndex: selectedIndex,
				isShowCostingPanel: isShowCosting,
				isShowCostingButton: true,
				costingBrandInfo: {
					brandId: costingDetails?.brandId,
					brandName: costingDetails?.brandName,
					singleQty: selectedQuantity || findSetItem.quantity,
					isBuyerRequest: true,
					initialCostingId: selectedInitialCostingId
				}
			})
		);
	};

	const clearFabricStatus = () => {
		return {
			type: 'SET_PRODUCT_DATA',
			payload: {
				key: 'productsWithoutBodyFabric',
				data: []
			}
		};
	};

	const onClearSelectedProducts = async () => {
		await dispatch(storeProductData('selectedProductIds', []));
		await dispatch(clearFabricStatus());
	};

	const fetchBuyerRequestPriceRange = async () => {
		try {
			const { data } = await ApiClient.GET('fetchBuyerRequestPriceRange', `${id}`);
			setBuyerPrice(data?.payload);
		} catch {
			//error;
		}
	};

	useEffect(() => {
		if (id) {
			fetchBuyerRequestPriceRange();
		}
	}, [id]);

	const formatPriceRange = (buyerPrice, productId) => {
		const priceData = buyerPrice?.[productId];
		if (!priceData?.medianPrice || !priceData?.avgPrice) return '';

		const median = priceData?.medianPrice ? parseFloat(priceData.medianPrice) : 0;
		const avg = priceData?.avgPrice ? parseFloat(priceData.avgPrice) : 0;
		return `$${Math.min(median, avg).toFixed(2)} - $${Math.max(median, avg).toFixed(2)}`;
	};

	// const getProductWithNoBodyFabric = () => {
	// 	const listOfProduct = [];
	// 	costingItem?.forEach((product) => {
	// 		if (selectedIds.includes(product?.quantityWiseCostingId.toString())) {
	// 			if (product?.isSet) {
	// 				return product?.setMemberProducts?.forEach((member) => {
	// 					if (!member.fabricDetails && !member.fabricName) {
	// 						listOfProduct.push(product);
	// 					}
	// 				});
	// 			} else {
	// 				if (!product.fabricDetails && !product.fabricName) {
	// 					listOfProduct.push(product);
	// 				}
	// 			}
	// 		}
	// 	});
	// 	return listOfProduct;
	// };

	// const getProductWithSoldArtwork = () => {
	// 	return selectedIds?.some((id) => {
	// 		const foundItem = costingItem?.find((item) => item.quantityWiseCostingId.toString() === id);
	// 		return foundItem && foundItem.status === 'SOLD';
	// 	});
	// };

	const isShowRepeatOrderTag = () => {
		const selectedItems = costingItem?.filter((item) =>
			selectedIds.includes(item?.quantityWiseCostingId.toString())
		);
		return selectedItems.some((item) => item.availabilityStatus === 'SOLD');
	};

	const renderSetItemsDescriptionColumn = (item) => {
		return (
			<td
				className={`${item.isSameVariant ? 'd-none' : ''} description-column`}
				rowSpan={item.noOfSameVariant || 1}
			>
				<ul className="with-bullet">
					{item.isSet &&
						item.setMemberList &&
						item.setMemberList.length > 0 &&
						item?.setMemberList.map((member) => (
							<li key={_getKey()}>{member?.variation || ''}</li>
						))}
				</ul>
			</td>
		);
	};

	const renderTableRow = (item, idx) => {
		return (
			<tr
				key={`${item.quantityWiseCostingId}_${idx}`}
				className={selectedQuoteItemId == item.id ? 'highlight-table-row' : ''}
			>
				<td className="select-box-column">
					<div className="custom-chekbox d-inline-block align-middle">
						<div className="form-group m-0">
							<input
								type="checkbox"
								id={`costingSheet_${item.quantityWiseCostingId}`}
								name={`costingSheet_${item.quantityWiseCostingId}`}
								data-quantitywiseid={item.quantityWiseCostingId}
								data-productid={item.productId}
								onChange={(e) => onChangeCheckbox(e, item)}
								checked={selectedIds?.find(
									(value) => value?.toString() === item?.quantityWiseCostingId.toString()
								)}
							/>
							<label htmlFor={`costingSheet_${item.quantityWiseCostingId}`} />
						</div>
					</div>
				</td>
				{/* design column */}
				<td
					className={`${item.isSameProduct ? ' d-none' : ''} cursor-pointer design-column`}
					rowSpan={item?.noOfSameProduct || 1}
					onClick={() => {
						setIsShowCosting(false);
						setSelectedIndex(idx);
						setSelectedProductId(item.productId);
						setSelectedInitialCostingId(item.initialCostingId);
					}}
				>
					{item.isSet ? (
						renderSetItemDesingDetailsWithImage(
							item?.setMemberList,
							costingRequestDetails,
							item?.productRefNo
						)
					) : (
						<div className="d-flex align-items-center">
							<div className="design-image">
								<img src={item?.featureImageDocUrl} alt="image" className="cell-img pr-2" />
							</div>
							<div
								className="ml-12"
								onClick={() => {
									setIsShowCosting(false);
									setSelectedIndex(idx);
									setSelectedProductId(item.productId);
									setSelectedInitialCostingId(item.initialCostingId);
								}}
							>
								{item?.isPack && packTag()}
								<p className="regular-12 gray_dark_01 py-1">{item?.productRefNo}</p>
								<ul className="common-list-component mb-2">
									{item?.market && <li>{item?.market}</li>}
									{item.category && <li>{item?.category} </li>}
								</ul>
								<ul className="common-list-component mb-1">
									{costingRequestDetails.requestedBy && (
										<li>{costingRequestDetails?.requestedBy}</li>
									)}
									{costingRequestDetails?.requestedDate && (
										<li>
											{changeDateFormat(
												costingRequestDetails.requestedDate,
												'',
												'Do MMM, YY'
											)}
										</li>
									)}
								</ul>
								{item?.requiredDate && (
									<p className="regular-12 mb-1">
										Required :
										<span className="gray_dark_01 weight-500 ml-1">
											{changeDateFormat(item?.requiredDate, '', 'Do MMM, YYYY')}
										</span>
									</p>
								)}
							</div>
							<div>
								{isShowDeleteButton() && item.isCloned && !item?.productionPrice && (
									<img
										onClick={(e) => {
											e.stopPropagation();
											onDeleteQuoteItem(item.id);
										}}
										src={`${process.env.PUBLIC_URL}/icons/delete_gray.svg`}
										alt="delete"
										className="delete-action"
									/>
								)}
							</div>
						</div>
					)}
				</td>
				{/* description column */}
				{item.isSet ? (
					renderSetItemsDescriptionColumn(item, idx)
				) : (
					<td
						className={`cursor-pointer ${item.isSameVariant ? 'd-none' : ''} description-column`}
						rowSpan={item?.noOfSameVariant || 1}
						onClick={() => {
							!item.isQuoted &&
								!item.isSet &&
								onSetCellId(item.quantityWiseCostingId, 'VARIANT');
						}}
					>
						<>
							{cellId === `${item.quantityWiseCostingId}_VARIANT` ? (
								<textarea
									name="variation"
									value={item?.variation}
									autoFocus={true}
									rows="5"
									cols="50"
									onFocus={(event) => (prevDescription.current = event.target.value)}
									onBlur={(event) => {
										if (!isValidInput(prevDescription, event)) {
											resetAll();
											return;
										}
										onUpdateVariant({
											itemId: item.id,
											variant: item.variation
										});
									}}
									onChange={(e) => onChangeItem(e, item)}
								/>
							) : (
								<span className="cursor-pointer">{item?.variation || ''}</span>
							)}
						</>
						{/* add variant Plus Icon button */}
						{!item?.isQuoted && (
							<div>
								<img
									onClick={(e) => {
										e.stopPropagation();
										onAddNewVariant(item);
									}}
									src={`${process.env.PUBLIC_URL}/icons/plus_dark_32.svg`}
									alt
									className="action-icon"
								/>
								{/* Remove Variant Minus Icon Button */}
								{item?.isSameProduct && (
									<img
										onClick={(e) => {
											e.stopPropagation();
											onRemoveVariant(item);
										}}
										src={`${process.env.PUBLIC_URL}/icons/minus_dark_32.svg`}
										alt="minus-icon"
										className="action-icon minus"
									/>
								)}
							</div>
						)}
					</td>
				)}
				{/* qty column */}
				<td
					className="cursor-pointer qty-column"
					onClick={() => {
						setIsShowCosting(true);
						setSelectedIndex(idx);
						setSelectedProductId(item.productId);
						setSelectedInitialCostingId(item.initialCostingId);
					}}
				>
					<span className="regular-14 gray_dark_01">{item?.quantity}</span>
				</td>
				<td className="buyer-price-range">
					<span className="regular-14 gray_dark_01">
						{formatPriceRange(buyerPrice, item.productId)}
					</span>
				</td>
				{/* Target Column */}
				<td
					className="target-column"
					onClick={() =>
						!isCostingManger() && onSetCellId(item.quantityWiseCostingId, 'TARGET_PRICE')
					}
				>
					{cellId === `${item.quantityWiseCostingId}_TARGET_PRICE` ? (
						<input
							type="number"
							autoComplete="off"
							name="target"
							value={item.target || ''}
							placeholder="$00.00"
							onKeyPress={validateFloatNumber}
							autoFocus
							onFocus={(event) => (prevTarget.current = event.target.value)}
							onBlur={(event) => {
								if (!isValidInput(prevTarget, event)) {
									resetAll();
									return;
								}
								onUpdateItem({
									id: item.quantityWiseCostingId,
									targetPrice: parseFloat(item.target) || null
								});
							}}
							onChange={(e) => onChangeItem(e, item)}
							onKeyDown={(e) => {
								if (e.key === 'Enter') {
									if (!isValidInput(prevTarget, e)) {
										resetAll();
										return;
									}
									onUpdateItem({
										id: item.quantityWiseCostingId,
										targetPrice: parseFloat(item.target) || null
									});
								}
							}}
						/>
					) : (
						<span className="regular-14 gray_dark_01"> {item?.target && `$${item?.target}`}</span>
					)}
				</td>
				{/* Production Price */}
				<td
					className="production-price-column cursor-pointer"
					onClick={() => {
						setIsShowCosting(true);
						setSelectedIndex(idx);
						setSelectedProductId(item.productId);
						setSelectedInitialCostingId(item.initialCostingId);
						setSelectedQuantity(item?.quantity);
					}}
				>
					<span className="regular-14 gray_dark_01">
						{item?.productionPrice ? (
							`$${parseFloat(item?.productionPrice).toFixed(2)}`
						) : (
							<span className="gray_dark_02 add-price">Add price</span>
						)}
						{item?.isQuoted && item.productionPrice > 0 && !item?.isFactoryCosting && (
							<img
								className="pl-2"
								src={`${process.env.PUBLIC_URL}/icons/shiped.svg`}
								alt="quoted"
							/>
						)}
						{item?.isQuoted && item?.isFactoryCosting && (
							<img
								className="pl-2"
								src={`${process.env.PUBLIC_URL}/icons/factory.svg`}
								alt="factory"
							/>
						)}
					</span>
				</td>
				{/* offer Price */}
				{!isCostingManger() && (
					<td
						onClick={() => onSetCellId(item.quantityWiseCostingId, 'OFFER_PRICE')}
						className="offer-price-column"
					>
						{cellId === `${item.quantityWiseCostingId}_OFFER_PRICE` ? (
							<input
								type="number"
								autoComplete="off"
								name="margin"
								value={item?.margin || ''}
								placeholder="Margin %"
								onKeyPress={validateFloatNumber}
								autoFocus
								onFocus={(event) => (prevOffer.current = event.target.value)}
								onBlur={(event) => {
									if (!isValidInput(prevOffer, event)) {
										resetAll();
										return;
									}
									onUpdateItem({
										id: item.quantityWiseCostingId,
										margin: parseFloat(item.margin) || null
									});
								}}
								onChange={(e) => onChangeItem(e, item)}
								onKeyDown={(e) => {
									if (e.key === 'Enter') {
										onUpdateItem({
											id: item.quantityWiseCostingId,
											margin: parseFloat(item.margin) || null
										});
									}
								}}
							/>
						) : (
							getOfferPrice(item)
						)}
					</td>
				)}
				<td
					className="status-column cursor-pointer"
					onClick={(e) => {
						e.stopPropagation();
						setShowCostingTimelineId(item?.id);
					}}
				>
					<div
						className={`sample-status-with-details ${showCostingTimelineId === item?.id ? 'timeline-opened' : ''}`}
					>
						<div className="sample-status">
							{getColorWiseActivityStatusOfCosting(
								item?.currentStatus?.status,
								(['APPROVED', 'REJECTED'].includes(item?.currentStatus?.status)
									? 'Costing '
									: '') +
									originalStatus[item?.currentStatus?.status] +
									getActionDoneBy(item?.currentStatus?.userType || null)
							)}
						</div>

						<p>{changeDateFormat(item?.currentStatus?.dateTime, '', 'Do MMM, YYYY')}</p>
						{showCostingTimelineId === item?.id && (
							<CostingTimeline
								requiredDate={item?.requiredDate}
								activityList={item?.timeLineResponseList}
								onCloseTimeline={(e) => {
									e?.stopPropagation();
									setShowCostingTimelineId(null);
								}}
							/>
						)}
					</div>
				</td>
				{/* EOD Column */}
				<td
					className={`${item.isSameProduct ? 'd-none' : ''} cursor-pointer`}
					rowSpan={item?.noOfSameProduct || 1}
				>
					{item?.expectedOrderDate && changeDateFormat(item?.expectedOrderDate, '', 'Do MMM, YY')}
				</td>
				{/* Remarks Column */}
				<td
					className={`${item.isSameProduct ? 'd-none' : ''} remarks-column cursor-pointer`}
					rowSpan={item?.noOfSameProduct || 1}
					onClick={() => onSetCellId(item.quantityWiseCostingId, 'REMARKS')}
				>
					{cellId === `${item.quantityWiseCostingId}_REMARKS` ? (
						<Textarea
							autoComplete="off"
							name="remarks"
							value={item.remarks || ''}
							autoFocus
							onBlur={(event) => {
								if (!isValidInput(prevRemark, event)) {
									resetAll();
									return;
								}
								onUpdateItem({
									id: item.quantityWiseCostingId,
									remarks: item.remarks
								});
							}}
							onChange={(e) => onChangeItem(e, item)}
							onKeyDown={(e) => {
								if (e.key === 'Enter') {
									if (!isValidInput(prevRemark, e)) {
										resetAll();
										return;
									}
									onUpdateItem({
										id: item.quantityWiseCostingId,
										remarks: item.remarks
									});
								}
							}}
						/>
					) : (
						<span className="regular-14 gray_dark_02">{item?.remarks}</span>
					)}
				</td>
				{/* Conversation Column */}
				<td
					className={`${item.isSameProduct ? 'd-none' : ''} cursor-pointer`}
					rowSpan={item?.noOfSameProduct || 1}
				>
					<div className="conversation-btn">
						<button
							className="button text"
							onClick={() => {
								dispatch(
									setCurrentActivePage({
										id: item?.productId,
										type: 'PRODUCT_CONVERSATION',
										pageType: 'DETAILS'
									})
								);
								setSelectedId(item?.productId);
								setShowComment(true);
							}}
						>
							{item?.noOfUnreadMessage > 0 ? (
								<img
									src={`${process.env.PUBLIC_URL}/icons/comment-not-read.svg`}
									alt="not read"
								/>
							) : (
								<img
									src={`${process.env.PUBLIC_URL}/icons/comments-gray.svg`}
									alt="not read"
								/>
							)}
						</button>
					</div>
				</td>
			</tr>
		);
	};

	const onApproveOrRejct = async (type) => {
		try {
			dispatch(openLoader());
			type === 'APPROVE' ? await onApproveRequest(selectedIds) : await onRejectRequest(selectedIds);
			fetchCostingDetails();
			dispatch(closeLoader());
		} catch (error) {
			dispatch(closeLoader());
			toastError(error.response.data.message);
		}
	};

	const onSuccessEodUpdate = () => {
		fetchCostingDetails();
		setShowEodModal(false);
		setSelectedIds([]);
		setSelectedProductIds([]);
	};

	const notSimilarStatus = 'You have to select styles from similar status';
	const onCollspseFilter = () => setIsShowFilter(!isShowFilter);

	const checkIfAllProductionPriceAvailable = () => {
		const selectedItems = costingItem?.filter((item) =>
			selectedIds.includes(item?.quantityWiseCostingId.toString())
		);
		const allUpdated = selectedItems.every((item) => item?.productionPrice);
		return allUpdated;
	};

	const checkIfSameSimilarStatus = () => {
		const selectedItems = costingItem?.filter((item) =>
			selectedIds.includes(item?.quantityWiseCostingId.toString())
		);
		const firstVal = selectedItems[0]?.currentStatus?.status;
		const differentVal = selectedItems?.find((item) => item.currentStatus?.status !== firstVal)
			?.currentStatus?.status;
		if (differentVal) {
			return 'DIFFERENT';
		} else {
			return firstVal;
		}
	};

	const checkIfDisable = (name) => {
		return checkIfSameSimilarStatus() === replaceSpace(name)?.toUpperCase() ? false : true;
	};

	const onCloseModal = () => {
		setShowEodModal(false);
		setSelectedProductIds([]);
		setSelectedIds([]);
	};

	return (
		<>
			<>
				<div className="top-header transparent-filter">
					<div className="filter-container-top pt-2 pb-2 bg-gray">
						<div className="filters">
							<div className="sample-full-status d-flex align-items-center">
								<ul className="common-list-component">
									<li>
										<img
											className="cursor-pointer"
											src={`${process.env.PUBLIC_URL}/icons/back-arrow-gray.svg`}
											alt="back"
											onClick={() => navigate(-1)}
										/>
										<span className="semibold-14 gray_dark_01 ml-2">
											{costingDetails?.title || costingDetails?.collectionName}
											<span className="regular-12 gray_dark_02 weight-400">
												({costingDetails?.referenceNumber})
											</span>
										</span>
									</li>

									<li>
										<span>{costingDetails?.requestedBy}</span>
									</li>
									<li>
										<span>
											{changeDateFormat(costingDetails?.requestedDate, '', 'Do MMM')}
										</span>
									</li>
									<li>
										<span className="brand-common-design">
											{costingDetails?.brandName}
										</span>
									</li>
								</ul>
								<span className="common-devider mx-3"></span>
								<button
									className="button text-only"
									onClick={() =>
										window.open(
											`${
												costingDetails?.folderId
													? `/favourite/${costingDetails?.folderId}`
													: `/collections/view/${costingDetails?.collectionId}`
											}`,
											'_blank'
										)
									}
								>
									<span className="blue">
										View Requested {costingDetails?.folderId ? 'Folder' : 'Collection'}
									</span>
								</button>
							</div>
						</div>
						<div className="filter-actions sample-dashboard-filter">
							<div className="add-buyer">
								<AddMemberToQuote quoteId={id} />
							</div>
						</div>
					</div>
					<CostingDetailsTopHeader toggleFilter={onCollspseFilter} elementRef={elementRef} />
				</div>
			</>

			<section className="filter-data-container filter-v3-data-container">
				<FilterComponent
					filterPageName={allFilterPages.COSTING_DETAILS}
					fields={costingDetailsFilterFields}
					showFilter={isShowFilter}
					isDependentFilter={true}
				/>

				<div className="data-table overflow-hidden">
					<div className="costing-dashboard-container">
						{costingDetails?.quoteItemResponseList?.length === 0 ? (
							<NoData message={'No costing found'} />
						) : (
							<div className="costing-details-view-table">
								<table
									className={`table with-border  scroll-x-label ${
										isCostingManger() ? 'costing-manager-view' : ''
									}`}
									ref={elementRef}
								>
									<thead>
										<tr>
											<th className="select-box-column">
												<div className="custom-chekbox d-inline-block align-middle">
													<div className="form-group m-0">
														<input
															type="checkbox"
															id="costingSheetAll"
															name="costingSheetAll"
															onChange={(e) => {
																setSelectAll(e.target.checked);
																markAll(e.target.checked);
															}}
															checked={isAllSelected}
														/>
														<label htmlFor="costingSheetAll"></label>
													</div>
												</div>
											</th>

											<th>Design({costingDetails?.noOfDesign})</th>
											<th>Description</th>
											<th>Qty</th>
											<th>Usual buying price</th>
											<th>Target</th>
											<th>Production price</th>
											{!isCostingManger() && <th>Offer price</th>}

											<th>Status & date</th>
											<th>Expected Order Date (EOD)</th>
											<th>Remarks</th>
											<th>Conversation</th>
										</tr>
									</thead>
									<tbody>
										{costingItem?.map((item, idx) => renderTableRow(item, idx))}
									</tbody>
								</table>
								<div className="col-md-1 one-sixth text-right">
									{selectedId && (
										<SampleComment
											sampleRequestId={costingDetails?.id || id}
											postTypeFilter={'PRODUCT_DEVELOPMENT_COMMENT'}
											topic={'PRODUCT_QUOTE_COMMENT'}
											postTypeId={'quoteRequestId'}
											productId={selectedId}
											showComment={showComment}
											setShowComment={() => handleCommentClose()}
											brandInfo={{
												brandId: costingDetails.brandId,
												brandName: costingDetails.brandName
											}}
										/>
									)}
								</div>
							</div>
						)}

						{/* Action Popup */}
						{selectedIds.length > 0 && (
							<div className="design-count-section costing-tab bg-white collection-details-design-count">
								<div className="count-info d-flex justify-content-between align-items-center">
									{/* Always visible elements */}
									<div className="design-count">
										<p className="regular-14">{selectedIds?.length}</p>
									</div>
									<button
										className="button text size36"
										onClick={() => {
											if (selectAll) {
												setSelectAll(false);
												markAll(false);
											} else {
												setSelectAll(true);
												markAll(true);
											}
										}}
									>
										<span className="ml-1">
											{isAllSelected === selectedIds?.length
												? 'Unselect All '
												: 'Select All'}
										</span>
									</button>

									{!isCostingManger() && (
										<>
											<span className="regular-16 gray_dark_01 d-inline-block mx-2  selection-devider">
												|
											</span>

											{checkIfSameSimilarStatus() === 'DIFFERENT' ? (
												<span className="regular-14 red d-flex align-items-center mt-1 mx-2">
													<img
														src={`${process.env.PUBLIC_URL}/icons/status-overdue.svg`}
														className="mr-2"
														alt="warning"
													/>
													{notSimilarStatus}
												</span>
											) : !checkIfAllProductionPriceAvailable() ? (
												<span className="regular-14 red d-flex align-items-center mt-1 mx-2">
													<img
														src={`${process.env.PUBLIC_URL}/icons/status-overdue.svg`}
														className="mr-2"
														alt="warning"
													/>
													{productionPriceMissing}
												</span>
											) : (
												<div className="count-info d-flex justify-content-between align-items-center">
													<>
														<button
															className="button text size36"
															onClick={() =>
																setConfirmationModal({
																	isOpen: true,
																	type: 'SEND_OFFER'
																})
															}
															disabled={selectedIds.length < 1}
														>
															<span>Send offer</span>
														</button>
														<button
															className="button text size36"
															onClick={() =>
																setConfirmationModal({
																	isOpen: true,
																	type: 'APPROVE'
																})
															}
															disabled={
																selectedIds.length < 1 ||
																checkIfDisable('OFFER_SENT')
															}
														>
															<span>Approved</span>
														</button>
														{/* <button
															className="button text size36"
															onClick={() => onApproveOrRejct('REJECT')}
															disabled={
																selectedIds.length < 1 ||
																checkIfDisable('OFFER_SENT')
															}
														>
															<span>Rejected</span>
														</button> */}

														{/* {costingDetails?.brandName !== 'Nitex' && (
															<button
																className="button text size36"
																onClick={onPlaceOrder}
																disabled={
																	!checkIfAllProductionPriceAvailable()
																}
															>
																<span className="mr-1">Place order</span>
															</button>
														)} */}

														{/* <button
															className="button text size36"
															onClick={onRequestClose}
															disabled={!isAllSelected}
														>
															<span>Close request</span>
														</button> */}
													</>

													<div
														className="text-right close-icon cursor-pointer"
														onClick={() => {
															setSelectedIds([]);
															setSelectAll(false);
														}}
													>
														<img
															src={`${process.env.PUBLIC_URL}/icons/close.svg`}
														/>
													</div>
												</div>
											)}

											<span className="regular-16 gray_dark_01 d-inline-block mx-2  selection-devider">
												|
											</span>

											{/* EOD and Cost sheet buttons - Always visible */}
											<button
												className="button text size36"
												onClick={() => setShowEodModal(true)}
											>
												<img
													src={`${process.env.PUBLIC_URL}/icons/calendar-dark.svg`}
													alt="download"
												/>
												<span className="ml-1">EOD</span>
											</button>
										</>
									)}
									<button
										className="button text size36"
										onClick={() => setIsShowTaskUpdateModal(true)}
									>
										<span className="mr-1">Update task</span>
									</button>

									<button className="button text size36" onClick={downloadSheet}>
										<img
											src={`${process.env.PUBLIC_URL}/icons/download-dark-20.svg`}
											alt="download"
										/>
										<span className="ml-1">Cost sheet</span>
									</button>

									{/* Close icon */}
									<div
										className="text-right close-icon cursor-pointer"
										onClick={() => {
											setSelectedIds([]);
											setSelectAll(false);
										}}
									>
										<img src={`${process.env.PUBLIC_URL}/icons/close.svg`} />
									</div>
								</div>
							</div>
						)}

						{confirmationModal.isOpen && (
							<ConfirmationModal
								title={confirmationModal.type === 'SEND_OFFER' ? 'Send Offer' : 'Approve'}
								description={
									confirmationModal.type === 'SEND_OFFER'
										? 'you want to send the offer for the selected items?'
										: 'you want to approve the selected items?'
								}
								closeModal={() => setConfirmationModal({ isOpen: false, type: null })}
								callback={() => {
									setConfirmationModal({ isOpen: false, type: null });
									confirmationModal.type === 'SEND_OFFER'
										? onSendOffer()
										: onApproveOrRejct('APPROVE');
								}}
							/>
						)}
						{isDownloading && <TimeBasedLoader startLoader={isDownloading} />}
						{showEodModal && (
							<EodAddModal
								selectedDesignIds={selectedProductIds}
								onCloseModal={onCloseModal}
								onSuccessEodUpdate={onSuccessEodUpdate}
							/>
						)}
						{isShowTaskUpdateModal && (
							<AddTaskModal
								onClose={() => {
									setSelectedId([]);
									setIsShowTaskUpdateModal(false);
								}}
								productIdList={selectedTaskProductIds}
								excludeTasks={[
									'COSTING_REJECTED',
									'BUYER_COSTING_REQUESTED_FROM_BUYER_PLATFORM',
									'CANCEL_BUYER_PLATFORM_COSTING_REQUEST',
									'BUYER_COSTING_REQUESTED_FROM_AM_BDM'
								]}
								journeyType={'COSTING'}
								quoteRequestId={id}
								excludeJourneyTypes={''}
							/>
						)}
						{isOpenPlaceOrder && (
							<CreateOrderModal
								isShowRepeatOrderTag={isShowRepeatOrderTag()}
								onClose={async () => {
									setIsOpenPlaceOrder(false);
									await onClearSelectedProducts();
								}}
								brandId={costingDetails?.brandId}
								collectionId={costingDetails?.collectionId}
							/>
						)}
					</div>
				</div>
			</section>
		</>
	);
};
export default CostingDetails;
